import clsx from 'clsx';
import {
  cloneElement,
  createElement,
  FunctionComponent,
  HTMLProps,
  PropsWithChildren,
  ReactElement,
  ReactHTML,
} from 'react';
import { useThemeColorVar } from '../../hooks/use-theme-color-var';
import { ThemeColorsValues, ThemeTypographyVariants } from '../../theme';
import { Typography } from '../typography';
import classes from './link.module.scss';

export type LinkProps<P extends object = HTMLProps<HTMLAnchorElement>>
  = PropsWithChildren<
    {
      /**
       * Custom typo variant
       */
      variant?: ThemeTypographyVariants;

      /**
       * Customize color
       */
      color?: ThemeColorsValues | 'inherit';

      /**
       * Customize render element
       */
      as?: FunctionComponent<P> | keyof ReactHTML;

      /**
       * Custom className
       */
      className?: string;

      /**
       * Custom prefix element
       */
      prefix?: ReactElement;

      /**
       Render typo as bold
       */
      bold?: boolean;
    } & Partial<Omit<P, 'as' | 'children' | 'prefix'>>
  >;

export function Link<P extends object = HTMLProps<HTMLAnchorElement>>({
  children,
  className,
  as = 'a',
  variant = 'inherit',
  prefix,
  color = 'primary',
  bold,
  ...props
}: LinkProps<P>) {
  const linkColor = useThemeColorVar(color);

  return createElement(
    as,
    {
      ...props,
      className: clsx(classes.root, className, 'link-component'),
      style: {
        '--link--color': linkColor,
      },
    } as P,
    <>
      <Typography
        as="span"
        bold={bold}
        color={color}
        variant={variant}
        className={classes.typo}
      >
        {prefix && cloneElement(prefix, { className: classes.prefix })}
        {children}
      </Typography>
    </>
  );
}
